import { useCallback, useEffect, useMemo, useState } from 'react'
import { AgxRow } from '@urbanx/agx.ui-components2'
import { NswContractStage, QldContractStage } from '@urbanx/agx-ui-components'
import {
  CampaignDetailModelState,
  CoolingOffDetails,
  FormCondition,
  FormConditionCondition,
  StaffUpdateContractDatesRequest,
  useInvalidateUseStaffGetContractDates,
  useStaffGetContractDates,
  useStaffUpdateContractDates,
  WaiverOfCoolingOff,
} from '@urbanx/agx.campaigns.client'
import { MenuTitles, Pages } from 'constants/menu'
import { ErrorModal } from 'helpers/ErrorModal'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { isDirtyEqual } from 'utils/compares'
import { ContractSubPage } from '../ContractSubPage'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { ConditionPanel } from './panels/ConditionPanel'
import { AddCoolingOffPeriod } from './modals/AddCoolingOffPeriod'
import { CoolingOffPeriodPanel } from './panels/CoolingOffPeriodPanel'
import { AddSubjectToFinance } from './modals/AddSubjectToFinance'
import { AddBuildingAndPest } from './modals/AddBuildingAndPest'
import { AddBodyCorporateRecords } from './modals/AddBodyCorporateRecords'
import { ContractDatePanel } from './panels/ContractDatePanel'
import LoadingState from 'components/loadingState/LoadingState'
import { WaiverOfCoolingOffPanel } from './panels/WaiverOfCoolingOffPanel'
import { AddWaiverOfCoolingOff } from './modals/AddWaiverOfCoolingOff'
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo'
import RexDisconnectedModal from 'pages/campaigns/components/manageCampaign/RexDisconnectedModal'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { RexDisconnectedModalFields, RexDisconnectedModalFieldValue } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { isEqual } from 'lodash-es'

const ContractDates = () => {
  const { campaign } = useSelectedCampaign()
  const { contract, isDocuSigned } = useSelectedContract()
  const [isLoaded, setIsLoaded] = useState(false)
  const [, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetContractDates =
    useInvalidateUseStaffGetContractDates()

  const {
    data: contractDates,
    isPending,
    isError,
  } = useStaffGetContractDates(
    contract?.contractId || '',
    !!contract?.contractId
  )

  const [contractDate, setContractDate] = useState<Date | undefined>(
    contractDates?.contractDate
  )

  const [coolingOffDetails, setCoolingOffDetails] = useState<
    CoolingOffDetails | undefined
  >(contractDates?.coolingOffDetails)

  const [waiverOfCoolingOff, setWaiverOfCoolingOff] = useState<
    WaiverOfCoolingOff | undefined
  >(contractDates?.waiverOfCoolingOff)

  const [subjectToFinance, setSubjectToFinance] = useState<
    FormCondition | undefined
  >(contractDates?.subjectToFinance)

  const [buildingAndPestCondition, setBuildingAndPestCondition] = useState<
    FormCondition | undefined
  >(contractDates?.buildingAndPestCondition)

  const [bodyCorporateRecordsCondition, setBodyCorporateRecordsCondition] =
    useState<FormCondition | undefined>(
      contractDates?.bodyCorporateRecordsCondition
    )

  const [openRexDisconnectedModal, setOpenRexDisconnectedModal] = useState(false)
  const [rexModalChangedFields, setRexModalChangedFields] = useState<Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>>(new Map())

  useEffect(() => {
    setContractDate(contractDates?.contractDate)
    setCoolingOffDetails(contractDates?.coolingOffDetails)
    setWaiverOfCoolingOff(contractDates?.waiverOfCoolingOff)
    setSubjectToFinance(contractDates?.subjectToFinance)
    setBuildingAndPestCondition(contractDates?.buildingAndPestCondition)
    setBodyCorporateRecordsCondition(
      contractDates?.bodyCorporateRecordsCondition
    )
    setIsLoaded(!isPending)
  }, [contractDates, isPending])

  useEffect(() => {
    setValidate(
      !isDirtyEqual(
        contractDate?.toString(),
        contractDates?.contractDate?.toString()
      ) ||
      !isDirtyEqual(coolingOffDetails, contractDates?.coolingOffDetails) ||
      !isDirtyEqual(waiverOfCoolingOff, contractDates?.waiverOfCoolingOff) ||
      !isDirtyEqual(subjectToFinance, contractDates?.subjectToFinance) ||
      !isDirtyEqual(
        buildingAndPestCondition,
        contractDates?.buildingAndPestCondition
      ) ||
      !isDirtyEqual(
        bodyCorporateRecordsCondition,
        contractDates?.bodyCorporateRecordsCondition
      )
    )
  }, [
    contractDate,
    coolingOffDetails,
    waiverOfCoolingOff,
    subjectToFinance,
    buildingAndPestCondition,
    bodyCorporateRecordsCondition,
  ])

  useEffect(() => {
    if (!openRexDisconnectedModal) {
      setRexModalChangedFields(new Map());
    }
  }, [openRexDisconnectedModal]);

  const { staffUpdateContractDates, staffUpdateContractDatesPending } =
    useStaffUpdateContractDates({
      onSuccess: () => {},
      onError: () => {},
    })

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '', campaign?.campaignId !== undefined);

  const isFormDirty = useMemo(() => {
    return (
      !isDirtyEqual(
        contractDate?.toString(),
        contractDates?.contractDate?.toString()
      ) ||
      !isDirtyEqual(coolingOffDetails, contractDates?.coolingOffDetails) ||
      !isDirtyEqual(waiverOfCoolingOff, contractDates?.waiverOfCoolingOff) ||
      !isDirtyEqual(subjectToFinance, contractDates?.subjectToFinance) ||
      !isDirtyEqual(
        buildingAndPestCondition,
        contractDates?.buildingAndPestCondition
      ) ||
      !isDirtyEqual(
        bodyCorporateRecordsCondition,
        contractDates?.bodyCorporateRecordsCondition
      )
    )
  }, [
    contractDate,
    coolingOffDetails,
    waiverOfCoolingOff,
    subjectToFinance,
    buildingAndPestCondition,
    bodyCorporateRecordsCondition,
    contractDates,
  ])

  const onSave = useCallback(
    async (success: () => void) => {
      if (!contract?.contractId) return

      if (!rexListing && isPendingRexListing === false) {
        const prevContractDate = contractDates?.contractDate;
        const newContractDate = contractDate;

        setRexModalChangedFields(prev => {
          const newMap = new Map(prev);

          if (!isEqual(prevContractDate?.toString(), newContractDate?.toString())) {
            newMap.set(RexDisconnectedModalFields.ContractDate, newContractDate?.toLocaleDateString() || '');
          }
          return newMap;
        });

        if (!isEqual(prevContractDate?.toString(), newContractDate?.toString())) {
          setOpenRexDisconnectedModal(true);
        }
      }

      const request: StaffUpdateContractDatesRequest = {
        contractDate: contractDate,
        coolingOffDetails: coolingOffDetails,
        waiverOfCoolingOff: waiverOfCoolingOff,
        subjectToFinance: subjectToFinance,
        buildingAndPestCondition: buildingAndPestCondition,
        bodyCorporateRecordsCondition: bodyCorporateRecordsCondition,
      }

      await staffUpdateContractDates(
        {
          body: request,
          contractId: contract.contractId,
        },
        {
          onSuccess: async (data) => {
            if (data.success) {
              success()
              await invalidateUseStaffGetContractDates(contract.contractId)
            } else {
              setSubmitFormErrors(data.errors || [])
              setOpenErrorModal(true)
            }
          },
        }
      )
    },
    [
      contractDate,
      coolingOffDetails,
      waiverOfCoolingOff,
      subjectToFinance,
      buildingAndPestCondition,
      bodyCorporateRecordsCondition,
      contract?.contractId,
      staffUpdateContractDates,
    ]
  )

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaign?.campaignId,
    contract?.contractId
  )

  if (!isLoaded) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.CONDITIONS}
      subPages={getContractInformationSubPages()}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateContractDatesPending}
      isError={isError}
    >
      <ContractDatePanel
        id="contractDate"
        key={`ContractDatePanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Contract Date"
        contractDate={contractDate}
        onChange={(contractDate) => setContractDate(contractDate)}
        isDocuSigned={isDocuSigned}
      />
      {coolingOffDetails &&
        campaign?.state === CampaignDetailModelState.NSW && (
          <CoolingOffPeriodPanel
            key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
            contractDate={contractDate}
            coolingOffDetails={coolingOffDetails}
            onChange={(updatedCoolingOffDetails) =>
              setCoolingOffDetails(updatedCoolingOffDetails)
            }
            onDelete={() => setCoolingOffDetails(undefined)}
            hideEditButton={
              !contract?.contractStage ||
              ![
                NswContractStage.Accepted,
                NswContractStage.Unconditional,
              ].includes(contract.contractStage as NswContractStage)
            }
            hideDeleteButton={
              !contract?.contractStage ||
              ![
                NswContractStage.Accepted,
                NswContractStage.Unconditional,
              ].includes(contract.contractStage as NswContractStage)
            }
          />
        )}
      {waiverOfCoolingOff &&
        campaign?.state === CampaignDetailModelState.QLD && (
          <WaiverOfCoolingOffPanel
            key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
            contractDate={contractDate}
            waiverOfCoolingOff={waiverOfCoolingOff}
            onChange={(updatedWaiverOfCoolingOff) =>
              setWaiverOfCoolingOff(updatedWaiverOfCoolingOff)
            }
            onDelete={() => setWaiverOfCoolingOff(undefined)}
            hideEditButton={
              !contract?.contractStage ||
              ![
                QldContractStage.Accepted,
                QldContractStage.Unconditional,
              ].includes(contract.contractStage as QldContractStage)
            }
            hideDeleteButton={
              !contract?.contractStage ||
              ![
                QldContractStage.Accepted,
                QldContractStage.Unconditional,
              ].includes(contract.contractStage as QldContractStage)
            }
          />
        )}

      {subjectToFinance?.condition &&
        subjectToFinance.condition !==
        FormConditionCondition.NotSubjectToFinance && (
          <ConditionPanel
            id="subjectToFinance"
            key={`SubjectToFinancePanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Finance"
            contractDate={contractDate}
            condition={subjectToFinance}
            onChange={(condition) => {
              setSubjectToFinance(condition as FormCondition | undefined)
            }}
            onDelete={() => setSubjectToFinance(undefined)}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {buildingAndPestCondition?.condition &&
        buildingAndPestCondition.condition !==
        FormConditionCondition.NotApplicable && (
          <ConditionPanel
            id="buildingAndPestCondition"
            key={`BuildingAndPestConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Building & Pest"
            contractDate={contractDate}
            condition={buildingAndPestCondition}
            onChange={(condition) => {
              setBuildingAndPestCondition(
                condition as FormCondition | undefined
              )
            }}
            onDelete={() => setBuildingAndPestCondition(undefined)}
            onDateConditions={[FormConditionCondition.OnSpecificDate]}
            fromDateConditions={[FormConditionCondition.NdaysFromDate]}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {bodyCorporateRecordsCondition?.condition &&
        bodyCorporateRecordsCondition.condition !==
        FormConditionCondition.NotApplicable && (
          <ConditionPanel
            id="bodyCorporateRecordsCondition"
            key={`BodyCorporateRecordsConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Body Corporate Records"
            contractDate={contractDate}
            condition={bodyCorporateRecordsCondition}
            onChange={(condition) => {
              setBodyCorporateRecordsCondition(
                condition as FormCondition | undefined
              )
            }}
            onDelete={() => setBodyCorporateRecordsCondition(undefined)}
            onDateConditions={[FormConditionCondition.OnSpecificDate]}
            fromDateConditions={[FormConditionCondition.NdaysFromDate]}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {!isPending && (
        <AgxRow gap="spacing-md" justifyContent="left" alignItems="flex-start">
          {!coolingOffDetails &&
            campaign?.state === CampaignDetailModelState.NSW && (
              <AddCoolingOffPeriod
                id="add-cooling-off"
                onConfirm={setCoolingOffDetails}
              />
            )}
          {!waiverOfCoolingOff &&
            campaign?.state === CampaignDetailModelState.QLD && (
              <AddWaiverOfCoolingOff
                id="add-cooling-off"
                onConfirm={setWaiverOfCoolingOff}
              />
            )}
          {(!subjectToFinance?.condition ||
            subjectToFinance.condition ===
            FormConditionCondition.NotSubjectToFinance) && (
              <AddSubjectToFinance
                id="add-finance"
                onConfirm={setSubjectToFinance}
              />
            )}
          {(!buildingAndPestCondition?.condition ||
            buildingAndPestCondition.condition ===
            FormConditionCondition.NotApplicable) && (
              <AddBuildingAndPest
                id="add-building-and-pest"
                onConfirm={setBuildingAndPestCondition}
              />
            )}
          {(!bodyCorporateRecordsCondition?.condition ||
            bodyCorporateRecordsCondition.condition ===
            FormConditionCondition.NotApplicable) && (
              <AddBodyCorporateRecords
                id="add-body-corporate-records"
                onConfirm={setBodyCorporateRecordsCondition}
              />
            )}
        </AgxRow>
      )}

      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {openRexDisconnectedModal ? (
        <RexDisconnectedModal
          setIsDialogOpen={setOpenRexDisconnectedModal}
          changedFields={rexModalChangedFields}
        />
      ) : (
        <></>
      )}
    </ContractSubPage>
  )
}

export default ContractDates
